import React, { useEffect, useState } from "react";

import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import {
  useDeleteCustomFieldMutation,
  useReorderCustomFieldsMutation,
} from "../../slices/CustomFieldsSlice";
import { apiErrorText, DeleteFieldModal } from "./CustomFieldModals";
import { CustomField, CustomFieldEntity } from "./customFields.types";
import { moveField } from "./customFields.utils";

/**
 * One list of fields (organizations or individuals) with drag-to-reorder and
 * delete (with a confirmation). Used by Settings and the client Edit window.
 */
export const useFieldList = (
  entityType: CustomFieldEntity,
  fields: CustomField[],
) => {
  const orgId = getLinkedFirm()?.orgId || "";
  const [order, setOrder] = useState<string[]>(fields.map((f) => f.id));
  const [dragId, setDragId] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<CustomField | null>(null);
  const [reorder] = useReorderCustomFieldsMutation();
  const [deleteField, { isLoading: deleting, error: deleteError, reset }] =
    useDeleteCustomFieldMutation();

  const fieldKey = fields.map((f) => f.id).join(",");
  useEffect(() => {
    if (!dragId) setOrder(fields.map((f) => f.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldKey]);

  const ordered = order
    .map((id) => fields.find((f) => f.id === id))
    .filter((f): f is CustomField => !!f)
    .concat(fields.filter((f) => !order.includes(f.id)));

  const finishDrag = () => {
    if (!dragId) return;
    setDragId(null);
    const before = fields.map((f) => f.id).join(",");
    if (order.join(",") !== before)
      reorder({ orgId, entityType, fieldIds: order });
  };

  const dragProps = (field: CustomField) => ({
    row: {
      onDragEnter: (event: React.DragEvent) => {
        event.preventDefault();
        if (dragId && dragId !== field.id)
          setOrder((current) => moveField(current, dragId, field.id));
      },
      onDragOver: (event: React.DragEvent) => event.preventDefault(),
      onDrop: (event: React.DragEvent) => {
        event.preventDefault();
        finishDrag();
      },
      dragging: dragId === field.id,
    },
    handle: {
      draggable: true,
      title: "Drag to reorder",
      onDragStart: (event: React.DragEvent) => {
        // eslint-disable-next-line no-param-reassign
        event.dataTransfer.effectAllowed = "move";
        try {
          event.dataTransfer.setData("text/plain", field.id);
        } catch {
          // Some browsers refuse; the drag still works.
        }
        setDragId(field.id);
      },
      onDragEnd: finishDrag,
    },
  });

  const deleteModal = toDelete ? (
    <DeleteFieldModal
      field={toDelete}
      deleting={deleting}
      error={
        deleteError
          ? apiErrorText(
              deleteError,
              "The field couldn’t be deleted. Please try again.",
            )
          : undefined
      }
      onCancel={() => {
        reset();
        setToDelete(null);
      }}
      onConfirm={async () => {
        const result = await deleteField({ id: toDelete.id });
        if ("data" in result) setToDelete(null);
      }}
    />
  ) : null;

  return { ordered, dragProps, askDelete: setToDelete, deleteModal };
};
